import { FocusMonitor } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  LOCALE_ID,
  AfterViewInit,
  OnDestroy,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import {
  MAT_FORM_FIELD,
  MatFormField,
  MatFormFieldControl,
} from '@angular/material/form-field';
import { MatTimepicker, MatTimepickerModule } from '@angular/material/timepicker';
import { ProductConfigurationService } from '@fleet/product-configuration';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import {
  composeDateTime,
  decomposeDateTime,
  formatMeridiemDisplay,
  isHourInRange,
  isMinuteInRange,
  isMinuteOnInterval,
  Meridiem,
  normalize12HourSegmentsOnHourExit,
  parseMeridiemFromInput,
  resolveEffectiveTimeDisplayMode,
  snapMinuteToInterval,
  TimeDisplayMode,
  TimeOverride,
  tryParsePastedTime,
} from './time-input.utilities';

@Component({
  selector: 'fleet-time-input',
  standalone: true,
  templateUrl: './time-input.component.html',
  styleUrls: ['./time-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block w-full min-w-0',
  },
  imports: [
    ReactiveFormsModule,
    MatTimepickerModule,
    MatLuxonDateModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TimeInputComponent),
      multi: true,
    },
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => TimeInputComponent),
    },
  ],
})
export class TimeInputComponent
  implements
    ControlValueAccessor,
    Validator,
    MatFormFieldControl<DateTime | null>,
    AfterViewInit,
    OnDestroy
{
  readonly displayMode = input<TimeDisplayMode>('locale');
  readonly intervalSeconds = input<number | null>(null);
  readonly timePicker = input<MatTimepicker<DateTime> | undefined>(undefined);

  readonly hourSegment = signal('');
  readonly minuteSegment = signal('');
  readonly meridiemDisplaySegment = signal('');

  protected readonly bridgeControl = new FormControl<DateTime | null>(null);

  readonly stateChanges = new Subject<void>();

  private static nextId = 0;
  readonly id = `fleet-time-input-${TimeInputComponent.nextId++}`;
  readonly controlType = 'fleet-time-input';
  readonly autofilled = false;
  readonly disableAutomaticLabeling = true;

  @ViewChild('hourInput') private hourInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('minuteInput') private minuteInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('meridiemInput') private meridiemInputRef?: ElementRef<HTMLInputElement>;

  private readonly locale = inject(LOCALE_ID);
  private readonly productConfiguration = inject(ProductConfigurationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);

  private readonly formField = inject(MAT_FORM_FIELD, { optional: true }) as
    | MatFormField
    | undefined;

  /** Set after DI stabilises — required public getter for `MatFormFieldControl`. */
  private resolvedNgControl: NgControl | null = null;

  get ngControl(): NgControl | null {
    return this.resolvedNgControl;
  }

  private onChangeFn: (value: DateTime | null) => void = () => undefined;
  private onTouchedFn: () => void = () => undefined;

  private accessorDisabled = false;
  private hostedFocused = false;
  private segmentTouched = false;
  private syncingFromOutside = false;
  private syncingFromBridge = false;
  private syncingToBridge = false;
  private ignoreNextNullWriteValue = false;

  private _placeholder = '';
  private _describedByIds: string[] = [];
  private readonly timeOverride = signal<TimeOverride | null>(null);

  readonly resolvedDisplayMode = computed(() =>
    resolveEffectiveTimeDisplayMode(
      this.displayMode(),
      this.locale,
      this.timeOverride()
    )
  );

  readonly use12Hour = computed(() => this.resolvedDisplayMode() === '12h');

  constructor() {
    this.productConfiguration.timeOverride$
      .pipe(takeUntilDestroyed())
      .subscribe((override) => {
        this.timeOverride.set(override);
        this.refreshSegmentsForDisplayMode();
        this.cdr.markForCheck();
      });

    this.focusMonitor
      .monitor(this.elementRef.nativeElement, true)
      .pipe(takeUntilDestroyed())
      .subscribe((origin) => {
        this.hostedFocused = !!origin;
        this.stateChanges.next();
        this.cdr.markForCheck();
      });

    this.bridgeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.syncingToBridge || this.syncingFromOutside) {
          return;
        }
        if (value?.isValid) {
          this.syncingFromBridge = true;
          try {
            this.applyDateTimeToSegments(value);
          } finally {
            this.syncingFromBridge = false;
          }
          this.emitComposedValue();
        }
        this.cdr.markForCheck();
      });

    effect((onCleanup) => {
      const picker = this.timePicker();
      if (!picker) {
        return;
      }
      const sub = picker.selected.subscribe((event) => {
        const value = event.value as DateTime;
        if (value?.isValid) {
          this.handlePickerValue(value);
        }
      });
      onCleanup(() => sub.unsubscribe());
    });

    queueMicrotask(() => {
      this.resolvedNgControl =
        this.injector.get(NgControl, null, { optional: true, self: true }) ??
        null;
      if (this.resolvedNgControl) {
        this.resolvedNgControl.valueAccessor = this;
      }
      const parentCtrl = this.resolvedNgControl?.control;
      if (parentCtrl) {
        parentCtrl.statusChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.stateChanges.next();
            this.cdr.markForCheck();
          });
      }
      this.stateChanges.next();
      this.cdr.markForCheck();
    });
  }

  get placeholder(): string {
    return this._placeholder;
  }

  get empty(): boolean {
    return !this.hourSegment() && !this.minuteSegment();
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get required(): boolean {
    return !!this.ngControl?.control?.hasValidator(Validators.required);
  }

  get disabled(): boolean {
    return this.accessorDisabled;
  }

  get focused(): boolean {
    return this.hostedFocused;
  }

  get errorState(): boolean {
    const control = this.ngControl?.control;
    return !!(control && control.invalid && this.showValidationErrors);
  }

  get showValidationErrors(): boolean {
    return (
      this.segmentTouched || !!this.ngControl?.control?.touched
    );
  }

  get value(): DateTime | null {
    return this.composeCurrentValue();
  }

  set value(dateTime: DateTime | null) {
    this.writeValue(dateTime);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.isTimeEntryComplete()) {
      return this.buildSegmentErrors();
    }

    if (!this.showValidationErrors) {
      return null;
    }

    if (this.segmentsHaveContent()) {
      return { invalidTime: true };
    }

    if (this.required) {
      return { invalidTime: true };
    }

    return null;
  }

  setDescribedByIds(ids: string[]): void {
    this._describedByIds = [...ids];
    this.cdr.markForCheck();
  }

  focus(): void {
    this.focusDefaultSegment();
  }

  onContainerClick(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof Element && target.closest('.time-input-element')) {
      return;
    }
    this.focusDefaultSegment();
  }

  onSegmentPointerDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  onNumericSegmentPointerDown(event: MouseEvent): void {
    this.onSegmentPointerDown(event);
    const input = event.target as HTMLInputElement;
    if (input.value) {
      event.preventDefault();
      input.focus({ preventScroll: true });
      input.select();
    }
  }

  private refreshSegmentsForDisplayMode(): void {
    const composed = this.composeCurrentValue();
    if (composed?.isValid) {
      this.applyDateTimeToSegments(composed);
    }
    this.stateChanges.next();
  }

  private focusDefaultSegment(): void {
    if (!this.hourSegment()) {
      this.hourInputRef?.nativeElement?.focus();
      return;
    }
    if (!this.minuteSegment()) {
      this.minuteInputRef?.nativeElement?.focus();
      return;
    }
    this.hourInputRef?.nativeElement?.focus();
  }

  writeValue(value: DateTime | null): void {
    if (value === null && this.ignoreNextNullWriteValue) {
      return;
    }

    this.syncingFromOutside = true;
    try {
      if (value?.isValid) {
        this.applyDateTimeToSegments(value);
      } else {
        this.clearSegments();
      }
      this.syncBridgeFromSegments();
      this.syncSegmentDomFromSignals();
    } finally {
      this.syncingFromOutside = false;
    }
    this.stateChanges.next();
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    if (this.segmentsHaveContent()) {
      this.syncSegmentDomFromSignals();
      this.cdr.markForCheck();
    }
  }

  registerOnChange(fn: (value: DateTime | null) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.accessorDisabled = isDisabled;
    this.stateChanges.next();
    this.cdr.markForCheck();
  }

  markControlAsUntouched(): void {
    this.segmentTouched = false;
    this.resolvedNgControl?.control?.markAsUntouched({ onlySelf: true });
    this.stateChanges.next();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.elementRef.nativeElement);
    this.stateChanges.complete();
  }

  onGroupFocusIn(): void {
    this.stateChanges.next();
  }

  onGroupFocusOut(event: FocusEvent): void {
    if (
      !this.elementRef.nativeElement.contains(event.relatedTarget as Node)
    ) {
      this.segmentTouched = true;
      this.onTouchedFn();
      this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
      this.stateChanges.next();
      this.cdr.markForCheck();
    }
  }

  onHourInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 2);
    input.value = digits;
    this.hourSegment.set(digits);
    this.maybeAutoFocusMinute(digits);
    this.emitComposedValue();
  }

  onMinuteInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 2);
    input.value = digits;
    this.minuteSegment.set(digits);
    this.emitComposedValue();
  }

  onMeridiemInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.value) {
      this.clearMeridiem();
      this.emitComposedValue();
      return;
    }

    if (this.hasCompleteMeridiem()) {
      const display = this.meridiemDisplaySegment();
      if (input.value !== display) {
        input.value = display;
      }
      return;
    }

    const meridiem = parseMeridiemFromInput(input.value);
    if (meridiem) {
      this.setMeridiemDisplay(meridiem);
      this.emitComposedValue();
      return;
    }
    const letters = input.value.toLowerCase().replace(/[^apm]/g, '').slice(0, 2);
    input.value = letters;
    this.meridiemDisplaySegment.set(letters);
    this.emitComposedValue();
  }

  onMeridiemPaste(event: ClipboardEvent): void {
    const meridiem = parseMeridiemFromInput(
      event.clipboardData?.getData('text') ?? ''
    );
    if (!meridiem) {
      event.preventDefault();
      if (this.hasCompleteMeridiem()) {
        this.setMeridiemDisplay(this.meridiemDisplaySegment() as Meridiem);
      }
      return;
    }
    event.preventDefault();
    this.setMeridiemDisplay(meridiem);
    this.emitComposedValue();
  }

  onMeridiemPointerDown(event: MouseEvent): void {
    this.onSegmentPointerDown(event);
    if (this.hasCompleteMeridiem()) {
      const input = event.target as HTMLInputElement;
      event.preventDefault();
      this.focusInputWithSelectAll(input);
    }
  }

  onMeridiemBlur(): void {
    this.normalizeMeridiemOnBlur();
    this.finalizeOnBlur();
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  onMeridiemKeydown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    const input = this.meridiemInputRef?.nativeElement;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      if (!this.meridiemDisplaySegment()) {
        if (event.key === 'Backspace' && this.minuteInputRef) {
          event.preventDefault();
          this.minuteInputRef.nativeElement.focus();
        }
        return;
      }
      if (this.hasCompleteMeridiem() && input) {
        const fullSelected =
          input.selectionStart === 0 &&
          input.selectionEnd === input.value.length;
        if (fullSelected) {
          event.preventDefault();
          this.clearMeridiem();
          this.emitComposedValue();
          this.cdr.markForCheck();
          return;
        }
        event.preventDefault();
        this.setMeridiemDisplay(this.meridiemDisplaySegment() as Meridiem);
        return;
      }
      return;
    }

    if (key === 'a' || key === 'p') {
      event.preventDefault();
      this.setMeridiemDisplay(key === 'a' ? 'AM' : 'PM');
      this.emitComposedValue();
      this.cdr.markForCheck();
      return;
    }

    if (key === 'm' && !this.hasCompleteMeridiem()) {
      event.preventDefault();
      const partial = this.meridiemDisplaySegment().toLowerCase();
      this.setMeridiemDisplay(partial.startsWith('p') ? 'PM' : 'AM');
      this.emitComposedValue();
      this.cdr.markForCheck();
      return;
    }

    if (this.hasCompleteMeridiem()) {
      if (key.length === 1 && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        return;
      }
    } else if (
      key.length === 1 &&
      /[a-z]/i.test(event.key) &&
      key !== 'a' &&
      key !== 'p' &&
      key !== 'm'
    ) {
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowLeft' && input) {
      if (input.selectionStart === 0) {
        event.preventDefault();
        this.minuteInputRef?.nativeElement?.focus();
      }
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const current = this.resolvedMeridiem();
      const next: Meridiem = current === 'PM' ? 'AM' : 'PM';
      this.setMeridiemDisplay(next);
      this.emitComposedValue();
      this.cdr.markForCheck();
    }
  }

  onHourBlur(): void {
    this.apply12HourHourExitNormalization();
    this.normalizeHourOnBlur();
    this.finalizeOnBlur();
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  onMinuteBlur(): void {
    this.normalizeMinuteOnBlur();
    this.finalizeOnBlur();
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  onHourKeydown(event: KeyboardEvent): void {
    if (event.key === ':') {
      event.preventDefault();
      this.apply12HourHourExitNormalization();
      this.focusInputWithSelectAll(this.minuteInputRef?.nativeElement);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.adjustHour(event.key === 'ArrowUp' ? 1 : -1);
    }
  }

  onMinuteKeydown(event: KeyboardEvent): void {
    if (
      event.key === 'Backspace' &&
      !this.minuteSegment() &&
      this.hourInputRef
    ) {
      event.preventDefault();
      this.hourInputRef.nativeElement.focus();
      return;
    }
    if (event.key === 'ArrowLeft' && this.minuteInputRef) {
      const input = this.minuteInputRef.nativeElement;
      if (input.selectionStart === 0) {
        event.preventDefault();
        this.hourInputRef?.nativeElement?.focus();
      }
      return;
    }
    if (event.key === 'ArrowRight' && this.use12Hour() && this.meridiemInputRef) {
      const input = this.minuteInputRef?.nativeElement;
      if (input && input.selectionStart === input.value.length) {
        event.preventDefault();
        this.meridiemInputRef.nativeElement.focus();
      }
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.adjustMinute(event.key === 'ArrowUp' ? 1 : -1);
    }
  }

  onHourPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? '';
    const parsed = tryParsePastedTime(text, this.locale);
    if (!parsed) {
      return;
    }
    event.preventDefault();
    this.applyDateTimeToSegments(parsed);
    this.emitComposedValue();
    this.cdr.markForCheck();
  }

  private handlePickerValue(value: DateTime): void {
    this.syncingFromBridge = true;
    try {
      this.applyDateTimeToSegments(value.startOf('minute'));
    } finally {
      this.syncingFromBridge = false;
    }
    this.emitComposedValue();
    this.cdr.markForCheck();
  }

  private applyDateTimeToSegments(dateTime: DateTime): void {
    const parts = decomposeDateTime(dateTime, this.use12Hour());
    this.hourSegment.set(parts.hour);
    this.minuteSegment.set(parts.minute);
    if (this.use12Hour()) {
      this.setMeridiemDisplay(parts.meridiem);
    } else {
      this.meridiemDisplaySegment.set('');
    }
    this.syncSegmentDomFromSignals();
  }

  private syncSegmentDomFromSignals(): void {
    if (this.hourInputRef) {
      this.hourInputRef.nativeElement.value = this.hourSegment();
    }
    if (this.minuteInputRef) {
      this.minuteInputRef.nativeElement.value = this.minuteSegment();
    }
    if (this.meridiemInputRef && this.use12Hour()) {
      this.meridiemInputRef.nativeElement.value = this.meridiemDisplaySegment();
    }
  }

  private segmentsHaveContent(): boolean {
    return (
      this.hourSegment().length > 0 ||
      this.minuteSegment().length > 0 ||
      this.meridiemDisplaySegment().length > 0
    );
  }

  private isTimeEntryComplete(): boolean {
    if (!this.hourSegment() || !this.minuteSegment()) {
      return false;
    }
    if (this.use12Hour()) {
      return !!this.resolvedMeridiem();
    }
    return true;
  }

  private clearSegments(): void {
    this.hourSegment.set('');
    this.minuteSegment.set('');
    this.meridiemDisplaySegment.set('');
  }

  private resolvedMeridiem(): Meridiem | null {
    return parseMeridiemFromInput(this.meridiemDisplaySegment());
  }

  private setMeridiemDisplay(meridiem: Meridiem): void {
    const display = formatMeridiemDisplay(meridiem);
    this.meridiemDisplaySegment.set(display);
  }

  private clearMeridiem(): void {
    this.meridiemDisplaySegment.set('');
  }

  private hasCompleteMeridiem(): boolean {
    const display = this.meridiemDisplaySegment();
    return display === 'AM' || display === 'PM';
  }

  private composeCurrentValue(): DateTime | null {
    const hour = this.hourSegment();
    const minute = this.minuteSegment();
    if (!hour.trim() && !minute.trim()) {
      return null;
    }
    if (this.use12Hour()) {
      const meridiem = this.resolvedMeridiem();
      if (!hour.trim() || !minute.trim() || !meridiem) {
        return null;
      }
      return composeDateTime(hour, minute, meridiem, true);
    }
    if (!hour.trim() || !minute.trim()) {
      return null;
    }
    return composeDateTime(hour, minute, 'AM', false);
  }

  private emitToParent(value: DateTime | null): void {
    if (value?.isValid) {
      this.onChangeFn(value);
      return;
    }
    this.ignoreNextNullWriteValue = true;
    this.onChangeFn(null);
    queueMicrotask(() => {
      this.ignoreNextNullWriteValue = false;
    });
  }

  private emitComposedValue(): void {
    if (this.syncingFromOutside || this.syncingFromBridge) {
      return;
    }
    const composed = this.composeCurrentValue();
    this.syncBridgeFromSegments(composed);
    this.emitToParent(composed?.isValid ? composed : null);
    this.stateChanges.next();
    this.cdr.markForCheck();
  }

  /** Commit value to parent on blur (including invalid partial → null). */
  private finalizeOnBlur(): void {
    if (this.syncingFromOutside || this.syncingFromBridge) {
      return;
    }
    const composed = this.composeCurrentValue();
    this.syncBridgeFromSegments(composed);
    if (!this.segmentsHaveContent()) {
      this.emitToParent(null);
    } else {
      this.emitToParent(composed?.isValid ? composed : null);
    }
    this.stateChanges.next();
    this.cdr.markForCheck();
  }

  private syncBridgeFromSegments(composed?: DateTime | null): void {
    const value = composed ?? this.composeCurrentValue();
    if (this.syncingFromBridge) {
      return;
    }
    this.syncingToBridge = true;
    try {
      this.bridgeControl.setValue(value?.isValid ? value : null, {
        emitEvent: true,
      });
    } finally {
      this.syncingToBridge = false;
    }
  }

  private buildSegmentErrors(): ValidationErrors | null {
    const hour = this.hourSegment();
    const minute = this.minuteSegment();
    const meridiem = this.meridiemDisplaySegment();
    const hasAny =
      hour.length > 0 || minute.length > 0 || meridiem.length > 0;
    if (!hasAny) {
      return null;
    }
    const composed = this.composeCurrentValue();
    if (
      !composed ||
      !isHourInRange(hour, this.use12Hour()) ||
      !isMinuteInRange(minute) ||
      (this.use12Hour() &&
        hour.length > 0 &&
        minute.length > 0 &&
        !this.resolvedMeridiem())
    ) {
      return { invalidTime: true };
    }
    if (!isMinuteOnInterval(composed.minute, this.intervalSeconds())) {
      return { invalidInterval: true };
    }
    return null;
  }

  private apply12HourHourExitNormalization(): void {
    if (!this.use12Hour()) {
      return;
    }
    const normalized = normalize12HourSegmentsOnHourExit(
      this.hourSegment(),
      this.meridiemDisplaySegment(),
    );
    if (!normalized) {
      return;
    }
    this.hourSegment.set(normalized.hour);
    if (normalized.meridiem) {
      this.setMeridiemDisplay(normalized.meridiem);
    }
    this.syncSegmentDomFromSignals();
  }

  private normalizeHourOnBlur(): void {
    const hour = this.hourSegment();
    if (!hour) {
      return;
    }
    const hourNum = parseInt(hour, 10);
    if (Number.isNaN(hourNum)) {
      return;
    }
    const padded = String(hourNum).padStart(2, '0');
    this.hourSegment.set(padded);
  }

  private normalizeMinuteOnBlur(): void {
    const minute = this.minuteSegment();
    if (!minute) {
      return;
    }
    const minuteNum = parseInt(minute, 10);
    if (Number.isNaN(minuteNum)) {
      return;
    }
    const snapped = snapMinuteToInterval(minuteNum, this.intervalSeconds());
    const padded = String(snapped).padStart(2, '0');
    this.minuteSegment.set(padded);
  }

  private normalizeMeridiemOnBlur(): void {
    const meridiem = this.resolvedMeridiem();
    if (meridiem) {
      this.setMeridiemDisplay(meridiem);
    }
  }

  private maybeAutoFocusMinute(hourDigits: string): void {
    if (!hourDigits || !this.minuteInputRef) {
      return;
    }
    const minuteInput = this.minuteInputRef.nativeElement;
    const hourNum = parseInt(hourDigits, 10);
    if (hourDigits.length >= 2) {
      this.apply12HourHourExitNormalization();
      this.focusInputWithSelectAll(minuteInput);
      return;
    }
    if (this.use12Hour() && hourNum > 1) {
      this.apply12HourHourExitNormalization();
      this.focusInputWithSelectAll(minuteInput);
      return;
    }
    if (!this.use12Hour() && hourNum > 2) {
      this.focusInputWithSelectAll(minuteInput);
    }
  }

  private focusInputWithSelectAll(input: HTMLInputElement | undefined | null): void {
    if (!input) {
      return;
    }
    input.focus({ preventScroll: true });
    if (input.value) {
      input.select();
    }
  }

  private adjustHour(delta: number): void {
    const use12Hour = this.use12Hour();
    const max = use12Hour ? 12 : 23;
    const min = use12Hour ? 1 : 0;
    let hour = parseInt(this.hourSegment() || String(min), 10);
    if (Number.isNaN(hour)) {
      hour = min;
    }
    hour = hour + delta;
    if (hour > max) {
      hour = min;
    }
    if (hour < min) {
      hour = max;
    }
    const padded = String(hour).padStart(2, '0');
    this.hourSegment.set(padded);
    this.emitComposedValue();
    this.cdr.markForCheck();
  }

  private adjustMinute(delta: number): void {
    let minute = parseInt(this.minuteSegment() || '0', 10);
    if (Number.isNaN(minute)) {
      minute = 0;
    }
    minute = (minute + delta) % 60;
    if (minute < 0) {
      minute = 59;
    }
    if (this.intervalSeconds()) {
      minute = snapMinuteToInterval(minute, this.intervalSeconds());
    }
    const padded = String(minute).padStart(2, '0');
    this.minuteSegment.set(padded);
    this.emitComposedValue();
    this.cdr.markForCheck();
  }

  protected get ariaDescribedBy(): string | null {
    return this._describedByIds.length
      ? this._describedByIds.join(' ')
      : null;
  }

  protected get labelId(): string | null {
    return this.formField?.getLabelId() ?? null;
  }
}
